import { useId, useState } from 'react'
import { Button, Dialog } from '@bloo-team/ui'
import type { RemarkDialogProps } from './types'

/**
 * Add a remark to one signal (signals/add_signal_remark). The server keeps
 * one remark per signal — a new one replaces it — and a remarked signal
 * leaves the Unassigned tab. Prefilled with the current remark.
 */
export function RemarkDialog({ open, signal, busy, error, onCancel, onConfirm }: RemarkDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} title="Add remark" className="sp-close-dialog" isDismissable={!busy}>
      {open && signal ? <RemarkForm signal={signal} busy={busy} error={error} onCancel={onCancel} onConfirm={onConfirm} /> : null}
    </Dialog>
  )
}

function RemarkForm({
  signal,
  busy,
  error,
  onCancel,
  onConfirm,
}: Omit<RemarkDialogProps, 'open' | 'signal'> & {
  signal: NonNullable<RemarkDialogProps['signal']>
}) {
  const uid = useId()
  const [text, setText] = useState(signal.remark)
  const valid = text.trim().length > 0 && text.trim() !== signal.remark.trim()
  const confirm = () => {
    if (valid && !busy) onConfirm(text.trim())
  }
  return (
    <div className="hud-stack sp-form">
      <p className="sp-form-what">
        <b>{signal.detectionName || signal.uuid}</b>
      </p>
      <label className="hud-stack sp-field" htmlFor={`${uid}-remark`}>
        <span className="hud-eyebrow">remark</span>
        <textarea
          id={`${uid}-remark`}
          aria-label="Remark"
          className="bui-input"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {signal.remark ? <p className="q-facet-note">Replaces the current remark.</p> : null}
      {error ? (
        <p className="sp-form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hud-row sp-form-actions">
        <Button variant="ghost" size="sm" tone="neutral" isDisabled={busy} onPress={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" isDisabled={!valid || busy} onPress={confirm}>
          {busy ? 'Saving…' : 'Save remark'}
        </Button>
      </div>
    </div>
  )
}
